import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { isAllowedReturnUrl } from '../lib/utils';
import { PageSkeleton } from './ui/Spinner';

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();

  if (isLoading) return <PageSkeleton />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;

  return <>{children}</>;
}

export function GuestOnly({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);

  if (isLoading) return <PageSkeleton />;

  /* If coming from cross-subdomain login, check for return_to */
  if (isAuthenticated) {
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('return_to');
    if (returnTo && isAllowedReturnUrl(returnTo)) {
      const token = localStorage.getItem('urblock_access_token');
      const refreshToken = localStorage.getItem('urblock_refresh_token');
      const url = new URL(returnTo);
      url.hash = `access_token=${token ?? ''}&refresh_token=${refreshToken ?? ''}`;
      window.location.href = url.toString();
      return null;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Restrict access to users with specific roles.
 * Redirects non-matching users to the home page.
 */
export function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  if (isLoading) return <PageSkeleton />;
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;

  return <>{children}</>;
}
